import { useRef, useEffect, useState, type ButtonHTMLAttributes } from 'react'
import { mtc } from '@/styles'
import { Badge } from '@/ui'
import { ADVISER_ASSISTANT_NAME } from '../lib/constants'
import { useAdviserAnalytics } from '../analytics'
import { GreetingBubble } from './GreetingBubble'
import { useChatTriggerAnimation } from './useChatTriggerAnimation'
import '../adviser-chat.scss'

const DISMISSED_KEY = 'adviser-greeting-dismissed'

/** Set on the floating trigger itself so a fixed footer elsewhere in the app
 *  can measure it and inset its own buttons out from under it. On the button
 *  rather than its positioning wrapper — the wrapper also holds the greeting
 *  bubble, which is far wider than the trigger and only sometimes present. */
export const HAVEN_TRIGGER_ATTRIBUTE = 'data-haven-trigger'

interface ChatTriggerProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  className?: string
  greetingTitle?: string
  greeting?: string
  greetingDelay?: number
  isThinking?: boolean
  hasNewMessage?: boolean
  // Only new users (per the sidebar restructure rollout cutoff) see this
  // greeting, so it never competes with the restructured sidebar's own
  // nav announcement shown to existing users.
  isNewUser?: boolean
  awaitingCount?: number
}

// Paths split from the seedling SVG so each can be animated independently.
// Leaf: upper-right sprout; vine: lower-left stem.
const LEAF_PATH =
  'M32 2C32 8.75 27.1875 14.375 20.875 15.6875C20.375 12.0625 18.75 8.8125 16.3125 6.3125C18.8125 2.5 23.125 0 28 0H30C31.125 0 32 0.875 32 2Z'
const VINE_PATH =
  'M0 6C0 4.875 0.875 4 2 4H4C11.75 4 18 10.25 18 18V30C18 31.125 17.125 32 16 32C14.875 32 14 31.125 14 30V20C6.25 20 0 13.75 0 6Z'

export const ChatTrigger = ({
  className,
  greetingTitle = `Meet ${ADVISER_ASSISTANT_NAME}.`,
  greeting = 'Ask for help with Heard, your finances, and more.',
  greetingDelay = 3000,
  isThinking = false,
  hasNewMessage = false,
  isNewUser = false,
  awaitingCount = 0,
  onClick,
  ...props
}: ChatTriggerProps) => {
  const [showGreeting, setShowGreeting] = useState(false)
  const { trackGreetingDismissed } = useAdviserAnalytics()

  useEffect(() => {
    if (!isNewUser) return undefined
    if (localStorage.getItem(DISMISSED_KEY)) return undefined
    const timer = setTimeout(() => setShowGreeting(true), greetingDelay)
    return () => clearTimeout(timer)
  }, [greetingDelay, isNewUser])

  const dismissGreeting = (e: React.MouseEvent) => {
    e.stopPropagation()
    setShowGreeting(false)
    localStorage.setItem(DISMISSED_KEY, '1')
    trackGreetingDismissed()
  }

  const buttonRef = useRef<HTMLButtonElement>(null)
  const svgRef = useRef<SVGSVGElement>(null)
  const vineRef = useRef<SVGPathElement>(null)
  const leafRef = useRef<SVGPathElement>(null)
  const glowRef = useRef<SVGCircleElement>(null)
  const hoverOverlayRef = useRef<HTMLSpanElement>(null)

  const { handleMouseEnter, handleMouseMove, handleMouseLeave, handleClick } =
    useChatTriggerAnimation(
      { buttonRef, vineRef, leafRef, glowRef, hoverOverlayRef },
      { isThinking, hasNewMessage, onClick }
    )

  return (
    // Transparent to the pointer, with only the bubble and the button itself
    // taking clicks back. This box is as wide as the greeting bubble and tall
    // enough to reach from it down past the trigger, and it sits above the
    // page — so as a solid target its empty regions (the gap between bubble
    // and button, the space beside the button) swallowed clicks on whatever
    // was underneath, most visibly onboarding's Continue button.
    <div className="pointer-events-none fixed bottom-6 right-6 z-40 flex flex-col items-end gap-3">
      {showGreeting && (
        <GreetingBubble
          title={greetingTitle}
          message={greeting}
          onDismiss={dismissGreeting}
        />
      )}
      <button
        ref={buttonRef}
        {...{ [HAVEN_TRIGGER_ATTRIBUTE]: true }}
        aria-label="Open Haven chat assistant"
        className={mtc(
          'pointer-events-auto relative flex items-center justify-center',
          'h-14 w-14 border-none cursor-pointer shadow-lg overflow-hidden',
          className
        )}
        style={{
          borderRadius: '60px 60px 4px 60px',
          backgroundColor: 'var(--adviser-icon-leaf)',
        }}
        onMouseEnter={handleMouseEnter}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
        {...props}
      >
        {awaitingCount > 0 && (
          <Badge
            aria-hidden="true"
            size="sm"
            className={mtc(
              'pointer-events-none absolute -right-1 -top-1 z-10',
              'h-5 min-w-5 justify-center rounded-full px-1 py-0',
              'text-[10px] font-semibold leading-none'
            )}
          >
            {awaitingCount > 99 ? '99+' : awaitingCount}
          </Badge>
        )}
        {/* Hover gradient overlay — dark top fading to transparent */}
        <span
          ref={hoverOverlayRef}
          aria-hidden="true"
          className="pointer-events-none absolute inset-0"
          style={{
            opacity: 0,
            background:
              'linear-gradient(180deg, rgba(0,0,0,0.22) 0%, rgba(0,0,0,0) 100%)',
            borderRadius: 'inherit',
          }}
        />
        <span
          className="adviser-trigger-leaf relative"
          style={{ color: 'var(--adviser-icon-seedling-fg)' }}
        >
          <svg
            ref={svgRef}
            viewBox="0 0 32 32"
            width="22"
            height="22"
            aria-hidden="true"
          >
            <defs>
              <filter
                id="haven-glow-filter"
                x="-80%"
                y="-80%"
                width="260%"
                height="260%"
              >
                <feGaussianBlur stdDeviation="1.8" result="blur" />
                <feMerge>
                  <feMergeNode in="blur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>
            <path ref={vineRef} d={VINE_PATH} fill="currentColor" />
            <path ref={leafRef} d={LEAF_PATH} fill="currentColor" />
            <circle
              ref={glowRef}
              cx="10"
              cy="28"
              r="3"
              fill="currentColor"
              opacity="0"
              filter="url(#haven-glow-filter)"
            />
          </svg>
        </span>
      </button>
    </div>
  )
}
